import {
  clearCart as clear,
  fetchCart as fetchCartData,
  addProductToCart,
  removeProductInCart,
  updateProductQtyInCart,
  updateSelectedProductsInCart,
  updateSelectedProductInCart,
  checkHasProductInCart,
  fetchCurrentDatetime,
} from '@/service/cart';
import { EStorageMedia } from '@/stores/cart';
import { unwrap, wrap } from '@uno-next/utils';
import dayjs, { Dayjs } from 'dayjs';
import type { ICart, ICartProduct } from '@/service/cart/types';
import { genRandomUUID } from '@/utils/random';

const CART_CACHE_KEY = '__cart_cache_key__';

const getCacheCart = () => unwrap<ICart>(localStorage.getItem(CART_CACHE_KEY) || '');
const setCacheCart = (data: ICart) => localStorage.setItem(CART_CACHE_KEY, wrap(data, 'object'));

export enum EGroup {
  TODAY = '今日加购',
  OTHER = '其他',
}

export const clearCartInCache = () => setCacheCart({});

class CartCache {
  public data: ICart;
  /** 服务器当前时间 */
  protected currentDate: string = Date.now().toString();
  protected now: Dayjs = dayjs();
  constructor(initialData: ICart) {
    this.data = initialData;

    fetchCurrentDatetime().then(({ currentTime }) => {
      this.currentDate = currentTime;
      this.now = dayjs(currentTime);
    });
  }

  /** 保存数据至 localStorage */
  save() {
    setCacheCart(this.data);
  }

  setData(data: ICart) {
    this.data = data;

    setCacheCart(data);
  }

  getData() {
    const { cartItemVOList = [] } = this.data;
    console.log(this.data);
    return {
      ...this.data,
      cartItemVOList: cartItemVOList.map(item => ({
        ...item,
        type: this.now.isSame((item as unknown as { createTime: string }).createTime, 'days')
          ? EGroup.TODAY
          : EGroup.OTHER,
      })),
    };
  }

  protected _genId() {
    return genRandomUUID(15);
  }

  protected genProduct(item: Omit<ICartProduct, 'id'>) {
    return {
      ...item,
      id: this._genId(),
      createTime: this.currentDate,
    };
  }

  add(item: Parameters<typeof addProductToCart>[number]) {
    const product = this.genProduct(item);
    // 确保数据格式
    if (!Array.isArray(this.data?.cartItemVOList)) {
      this.data.cartItemVOList = [];
    }

    const { newItem: isNewItem } = item;

    const { cartItemVOList = [] } = this.data;

    // 不合并商品 或者是当前购物车中无商品时
    if (isNewItem || cartItemVOList.length === 0) {
      this.data.cartItemVOList.push(product);
    } else {
      const mergedItem = cartItemVOList
        .filter(({ skuCode }) => skuCode === item.skuCode)
        .reduce((occ, cur) => ({ ...occ, ...cur, qty: occ.qty + cur.qty }), {
          qty: 0,
          createTime: this.currentDate,
        } as unknown as ICartProduct);

      mergedItem.id = this._genId();

      const filteredItems = cartItemVOList.filter(({ skuCode }) => skuCode !== item.skuCode);

      this.data.cartItemVOList = [...filteredItems, mergedItem];
    }

    this.save();
  }

  remove(id: string | string[]) {
    console.log(id);
    const { cartItemVOList = [] } = this.data;
    const idsMap = (Array.isArray(id) ? id : [id]).reduce(
      (occ, cur) => ({ ...occ, [cur]: true }),
      {}
    );
    const unremovedList = cartItemVOList.filter(({ id: itemId }) => !idsMap[itemId]);
    console.log(unremovedList);
    this.data.cartItemVOList = unremovedList;
    this.save();
  }

  updateQty(id: string, qty: number) {
    if (qty <= 0) {
      return;
    }
    const { cartItemVOList = [] } = this.data;

    const idx = cartItemVOList.findIndex(({ id: itemId }) => itemId === id);

    if (idx >= 0) {
      cartItemVOList[idx].qty = qty;

      this.save();
    }
  }

  updateSelected(id: string, checked: boolean) {
    const { cartItemVOList = [] } = this.data;

    const idx = cartItemVOList.findIndex(({ id: itemId }) => itemId === id);

    if (idx >= 0) {
      cartItemVOList[idx].checked = checked;
      this.save();
    }
  }

  updateAllSelected(checked: boolean) {
    const { cartItemVOList = [] } = this.data;

    const checkedList = cartItemVOList.map((item, index) => {
      return {
        ...item,
        checked: checked,
      }});
    this.data.cartItemVOList = checkedList;
    this.save();
  }

  clear() {
    this.setData({});
  }

  editForm(skuCode: String, productName: String, productBrand: String, productSize: String, id: String) {
    const { cartItemVOList = [] } = this.data;
    const editList = cartItemVOList.map((item, index) => {
      if(item.id === id) {
        console.log(skuCode);
        return {
          ...item,
          skuCode: skuCode,
          productBrand: productBrand,
          productSize: productSize,
          productName: productName,
        }
      } else {
        return item;
      }
    });
    this.data.cartItemVOList = editList;
    console.log('edit', skuCode);
    this.save();
  }

  hasProduct(sku: string | string[]) {
    const { cartItemVOList = [] } = this.data;
    const skuMap = (Array.isArray(sku) ? sku : [sku]).reduce(
      (occ, cur) => ({ ...occ, [cur]: true }),
      {}
    );

    return cartItemVOList.some(({ skuCode }) => skuMap[skuCode]);
  }
}

const cartCache = new CartCache(getCacheCart() || {});

export const fetchCart = (type: EStorageMedia) => {
  if (type === EStorageMedia.LOCAL) {
    return Promise.resolve(cartCache.getData());
  }

  return fetchCartData();
};

export const addProduct = async (
  type: EStorageMedia,
  data: Parameters<typeof addProductToCart>[number]
) => {
  if (type === EStorageMedia.LOCAL) {
    cartCache.add(data);

    return;
  }

  await addProductToCart(data);
};

export const removeProduct = async (type: EStorageMedia, ids: string | string[]) => {
  if (type === EStorageMedia.LOCAL) {
    return cartCache.remove(ids);
  }

  return removeProductInCart(Array.isArray(ids) ? ids : [ids]);
};

export const clearCart = async (type: EStorageMedia) => {
  if (type === EStorageMedia.LOCAL) {
    return cartCache.clear();
  }

  return clear();
};

export const updateProductQty = async (
  type: EStorageMedia,
  data: Parameters<typeof updateProductQtyInCart>[number]
) => {
  if (type === EStorageMedia.LOCAL) {
    return cartCache.updateQty(data.id, data.qty);
  }

  return updateProductQtyInCart(data);
};

export const updateAllChecked = async (
  type: EStorageMedia,
  data: Parameters<typeof updateSelectedProductsInCart>[number]
) => {
  if ( type === EStorageMedia.LOCAL) {
    return cartCache.updateAllSelected(data.checked);
  }
  const List = data.id.map(item => {
    return {
      id: item,
      checked: data.checked,
    }
  })
  return updateSelectedProductsInCart(List);
}

export const updateSelectedProduct = async (
  type: EStorageMedia,
  data: Parameters<typeof updateSelectedProductInCart>[number]
) => {
  if (type === EStorageMedia.LOCAL) {
    return cartCache.updateSelected(data.id, data.checked);
  }
  return updateSelectedProductInCart(data);
};

export const checkHasProduct = (
  type: EStorageMedia,
  skuCode: Parameters<typeof checkHasProductInCart>[number]
) => {
  if (type === EStorageMedia.LOCAL) {
    return cartCache.hasProduct(skuCode);
  }

  return checkHasProductInCart(skuCode);
};

export const editForm = (
  type: EStorageMedia,
  skuCode: String, productName: String, productBrand: String, productSize: String, id: String
) => {
  if(type === EStorageMedia.LOCAL) {
    return cartCache.editForm(skuCode, productName, productBrand, productSize, id);
  }
}